import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { RouterProvider } from "react-aria-components";
import { setPriorityStyle } from "@/components/domain/PriorityIcon";
import { PickerHost } from "@/components/items/PickerHost";
import { QuickCreateHost } from "@/components/items/QuickCreate";
import { useItemCommands } from "@/components/items/useItemCommands";
import { useNotificationCommands } from "@/components/notifications/useNotificationCommands";
import { AppShell } from "@/components/shell/AppShell";
import { CommandPalette } from "@/components/shell/CommandPalette";
import { RouteError } from "@/components/shell/RouteError";
import { ShortcutsSheet } from "@/components/shell/ShortcutsSheet";
import { SpaceDialogsHost } from "@/components/shell/spaceDialogs";
import { useCoreCommands } from "@/components/shell/useCoreCommands";
import { useWorkspaceCommands } from "@/components/shell/useWorkspaceCommands";
import { CatalogProvider } from "@/data/CatalogProvider";
import { startLive } from "@/data/live";
import { TitleTranslationProvider } from "@/data/titles";
import { applyLocale } from "@/lib/locale";
import { useCanAdmin } from "@/lib/permissions";
import { loadMe, loadSetupStatus, useMe } from "@/lib/queries";
import { syncPreferences } from "@/lib/serverPrefs";
import { useChangeLocale, useSignOut } from "@/lib/session";
import { useGlobalShortcuts } from "@/lib/shortcuts";

let localeAppliedFor: string | null = null;

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    const status = await loadSetupStatus(context.queryClient);
    if (status?.needsSetup) throw redirect({ to: "/setup" });

    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: "/login", search: { redirect: location.href } });
    // Instance policy: admins without two-factor may do nothing else (the API answers 403 two_factor_required).
    if (me.twoFactorRequired) throw redirect({ to: "/two-factor-setup" });

    // Instance-wide priority wording ("P0" or "Urgent"), before any view renders a label.
    setPriorityStyle(me.instance?.priorityStyle ?? "p");
    // The account language wins once per sign-in; later in-session switches are handled by the settings page.
    if (localeAppliedFor !== me.id) {
      localeAppliedFor = me.id;
      applyLocale(me.locale);
    }
    // Server-side preferences (theme, density, ...) win over this browser's cached ones; best effort, never blocks.
    await syncPreferences(me.id);
    // Live sync: read the event head before any view loads its rows (see data/live/manager.ts).
    await startLive(context.queryClient, me.id);
    return { me };
  },
  component: AppLayout,
  errorComponent: RouteError,
});

function AppLayout() {
  const { me: initial } = Route.useRouteContext();
  const live = useMe();
  const me = live.data ?? initial;
  const router = useRouter();
  const signOut = useSignOut();
  const changeLocale = useChangeLocale();

  const ctx = useMemo(() => ({ navigate: (to: string) => void router.navigate({ to: to as never }) }), [router]);

  const canAdmin = useCanAdmin();
  useCoreCommands({
    canAdmin,
    changeLocale: (locale) => changeLocale.mutate({ locale }),
    signOut: () => void signOut(),
  });
  useGlobalShortcuts(ctx);
  useItemCommands();
  useNotificationCommands();

  // Toast links (rendered above the router) ask for in-app navigation through this event.
  useEffect(() => {
    const go = (e: Event) => void router.navigate({ to: (e as CustomEvent<string>).detail as never });
    window.addEventListener("pt:navigate", go);
    return () => window.removeEventListener("pt:navigate", go);
  }, [router]);

  return (
    // React Aria links (tree items, menu items with href) navigate through the router.
    <RouterProvider
      navigate={(to) => void router.navigate({ to: to as never })}
      useHref={(to) => router.buildLocation({ to: to as never }).href}
    >
      <CatalogProvider>
        <TitleTranslationProvider>
          <Workspace />
          <AppShell me={me} />
          <CommandPalette ctx={ctx} />
          <ShortcutsSheet />
          <PickerHost />
          <QuickCreateHost />
          <SpaceDialogsHost />
        </TitleTranslationProvider>
      </CatalogProvider>
    </RouterProvider>
  );
}

/** Hooks that need the catalog (inside CatalogProvider). */
function Workspace() {
  useWorkspaceCommands();
  return null;
}
